import type { AppUser } from '../types';
import { isAdminUser, isActiveTester } from '../services/accessControl';

/**
 * Siste kjente admin/tester-rolle, husket per bruker.
 *
 * HVORFOR: rollen leses fra brukerdokumentet i Firestore. Blir DEN lesingen
 * blokkert (annonseblokker mot `firestore.googleapis.com`) eller treg (tracking
 * prevention som bremser IndexedDB), står appen igjen med en innlogget bruker
 * UTEN `isAdmin`/`isTester` — og da mister admin plutselig tilgangen til land
 * som ikke er lansert. Målt i produksjon 15.08.2026: konsollen viste
 * `{"sweden":true,"denmark":false}` for en konto som er admin i basen.
 *
 * Rollen huskes derfor per uid så snart vi ÉN gang har lest den, og brukes som
 * fallback når dokumentet ikke kan leses.
 *
 * SIKKERHET: dette styrer bare hva klienten VISER. Alle faktiske rettigheter
 * håndheves av Firestore-regler og av funksjonene, som leser rollen på nytt
 * server-side. Manipulerer noen localStorage, får de se plasser som uansett er
 * offentlige data — ingen skriverettigheter følger med.
 */

const NOKKEL = 'fricamp_role_memory';

export interface HusketRolle {
  isAdmin: boolean;
  isTester: boolean;
  activeTester: boolean;
}

const TOM: HusketRolle = { isAdmin: false, isTester: false, activeTester: false };

const les = (): Record<string, HusketRolle> => {
  try {
    const rå = localStorage.getItem(NOKKEL);
    return rå ? (JSON.parse(rå) as Record<string, HusketRolle>) : {};
  } catch {
    return {};
  }
};

const skriv = (alle: Record<string, HusketRolle>): void => {
  try { localStorage.setItem(NOKKEL, JSON.stringify(alle)); } catch { /* privat modus */ }
};

/** Rollen slik brukerdokumentet beskriver den akkurat nå. */
export const rolleFraBruker = (user?: AppUser | null): HusketRolle => ({
  isAdmin: isAdminUser(user),
  isTester: user?.isTester === true,
  activeTester: isActiveTester(user),
});

/**
 * Rollen vi skal bruke: det dokumentet sier, ellers det vi husker.
 *
 * Husker bare OPPGRADERINGER, aldri nedgraderinger — en rolle som forsvinner er
 * nesten alltid en mislykket lesing, mens en ekte fjerning av admin er sjelden
 * og løses ved å tømme lageret eller logge inn på nytt.
 */
export const resolveRole = (user?: AppUser | null): HusketRolle => {
  const uid = user?.uid;
  if (!uid) return TOM;

  const nå = rolleFraBruker(user);
  const lagret = les();
  // `=== true` overalt: eldre lagrede objekter kan mangle felt.
  const før: HusketRolle = {
    isAdmin: lagret[uid]?.isAdmin === true,
    isTester: lagret[uid]?.isTester === true,
    activeTester: lagret[uid]?.activeTester === true,
  };

  const slått: HusketRolle = {
    isAdmin: nå.isAdmin || før.isAdmin,
    isTester: nå.isTester || før.isTester,
    activeTester: nå.activeTester || før.activeTester,
  };

  if (slått.isAdmin !== før.isAdmin || slått.isTester !== før.isTester || slått.activeTester !== før.activeTester) {
    lagret[uid] = slått;
    skriv(lagret);
  }
  return slått;
};

/** Glem alt — brukes ved utlogging og av tester. */
export const forgetRoles = (): void => {
  try { localStorage.removeItem(NOKKEL); } catch { /* ignorer */ }
};
